import { useCallback, useContext, useEffect, useState } from 'react'

import { List } from 'antd'
import { isArray, uniq } from 'lodash'

import IndividualInternshipRequestCard from './IndividualInternshipRequestCard'
import InternshipRequestFilesDrawer from '../../institution/InternshipsView/InternshipRequestsView/InternshipRequestFilesDrawer'
import InternshipRequestButton from './InternshipRequestButton'
import { getIdOrNull } from '../../../utils'
import { getInstitutionOptionsEnabledByInstitution } from '../../../utils/api/institution'
import { InstitutionOptions, SchoolOptions } from '../../../utils/constants'
import { IndividualInternshipRequestModalContext } from '../../../Providers/Forms/IndividualInternshipRequest/IndividualInternshipRequestModal'
import { InternshipRequest, Origins } from '../../../utils/entities/internshipRequest'
import { RequestPortalContext } from '../../../Providers/RequestPortalProvider'
import { StudentContext } from '../../../Providers/Student/StudentProvider'

const InternshipRequestsList = ({ canRequestInternship, institution, internshipRequests, student, reload, onDelete }) => {
  const { setHandler } = useContext(IndividualInternshipRequestModalContext)
  const { portalAccessKey, setInstitution, setPortalAccessKey } = useContext(RequestPortalContext)
  const { schoolOptions } = useContext(StudentContext)

  /** Ids of the institutions requiring a convention, mapped to true */
  const [agreementInstitutions, setAgreementInstitutions] = useState({})

  const isConventionMandatory = useCallback(request => (
    request.requestOrigin !== Origins.MANUAL && (
      schoolOptions[getIdOrNull(request.school)]?.[SchoolOptions.AGREEMENT_FILE_MANDATORY_FOR_INTERNSHIP_REQUEST] === true ||
      agreementInstitutions[getIdOrNull(request.institution)] === true
    )
  ), [agreementInstitutions, schoolOptions])

  useEffect(() => {
    let active = true
    const institutionIds = uniq(internshipRequests.map(ir => getIdOrNull(ir.institution)).filter(id => id !== null))

    /** Without a portal, the institution answers with its other options instead : the type must be checked. */
    Promise.all(institutionIds.map(id => (
      getInstitutionOptionsEnabledByInstitution({ id }, [InstitutionOptions.AGREEMENT_FILE_MANDATORY_FOR_INTERNSHIP_REQUEST])
        .then(({ data }) => [id, isArray(data) && data.some(option => (
          option.optionType.type === InstitutionOptions.AGREEMENT_FILE_MANDATORY_FOR_INTERNSHIP_REQUEST && option.optionEnabled
        ))])
        .catch(() => [id, false])
    ))).then(entries => {
      if (active) {
        setAgreementInstitutions(Object.fromEntries(entries))
      }
    })

    return () => {
      active = false
    }
  }, [internshipRequests])

  useEffect(() => {
    if (canRequestInternship && getIdOrNull(institution) && getIdOrNull(student)) {
      setHandler(form => ({
        ...form,
        data: new InternshipRequest({ student, institution, requestOrigin: Origins.WEB_PORTAL }),
        /** The portal opens its institution to the student even when his school does not reach it. */
        portalAccessKey,
        callback: () => {
          reload()
          setInstitution(null)
          setPortalAccessKey(null)
        }
      }))
    }
  }, [canRequestInternship, institution, portalAccessKey, student])

  return (
    <div className='internship-requests-list h-100'>
      {internshipRequests.length > 0 && (
        <div className='overflow-y' style={{ height: 'calc(100% - 42px)' }}>
          <InternshipRequestFilesDrawer isConventionMandatory={isConventionMandatory}>
            <List>
              {internshipRequests.map(ir => (
                <List.Item key={ir.id}>
                  <IndividualInternshipRequestCard internshipRequest={ir} callback={reload} onDelete={onDelete} />
                </List.Item>
              ))}
            </List>
          </InternshipRequestFilesDrawer>
        </div>
      )}
      {canRequestInternship && <InternshipRequestButton student={student} reload={reload} />}
    </div>
  )
}

export default InternshipRequestsList
